import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { speak, stopSpeech } from "../utils/speechUtils";
import {
  Send,
  Mic,
  MicOff,
  Bot,
  User,
  Sparkles,
  AlertTriangle,
  Cpu,
  Loader2,
  FilePlus,
  RotateCcw,
  ChevronDown,
  Volume2,
  VolumeX,
  FileText,
  Check,
  ArrowRight,
} from "lucide-react";
import { assistantAPI, machinesAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import clsx from "clsx";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import HasPermission from "../components/HasPermission";
import { PERMISSIONS } from "../constants/roles";

const MessageBubble = ({ message, isUser, user }) => {
  const [isSpeaking, setIsSpeaking] = useState(false);

  const handleSpeak = () => {
    if (isSpeaking) {
      stopSpeech();
      setIsSpeaking(false);
    } else {
      setIsSpeaking(true);
      speak(
        message.content,
        {
          rate: 0.9,
          pitch: 1.05,
          voiceName: "Microsoft Zira",
        },
        () => setIsSpeaking(false)
      );
    }
  };

  const formattedTime = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  const userInitials =
    (user?.firstName?.[0] || "") + (user?.lastName?.[0] || (!user?.firstName ? "U" : ""));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        "flex gap-3 max-w-3xl",
        isUser ? "ml-auto flex-row-reverse" : "mr-auto"
      )}
    >
      {/* Avatar Container - matches Layout.jsx header logo for user */}
      <div
        className={clsx(
          "w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center flex-shrink-0 shadow-2xs",
          isUser
            ? "rounded-full bg-slate-700 text-slate-300 font-bold text-xs sm:text-sm border border-slate-600"
            : message.isError
              ? "rounded-xl bg-[#F3F4F6] text-[#E38A29] border border-[#E5E7EB]"
              : "rounded-xl bg-gradient-to-br from-[#3461D6] via-[#547EEA] to-[#4ABBE3] text-white shadow-[#3461D6]/20"
        )}
      >
        {isUser ? (
          <span>{userInitials}</span>
        ) : message.isError ? (
          <AlertTriangle className="w-4 h-4 text-[#E38A29]" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>

      {/* Bubble Container */}
      <div className={clsx("flex flex-col min-w-0 max-w-[85%] sm:max-w-[80%]", isUser && "items-end")}>
        {/* Header meta */}
        <div className="flex items-center gap-2 mb-1 px-1 text-[11px] font-semibold text-[#6B7280]">
          <span>
            {isUser
              ? user?.firstName
                ? `${user.firstName} ${user.lastName || ""}`.trim()
                : "You (Operator)"
              : "ConvoLink AI"}
          </span>
          {formattedTime && (
            <>
              <span className="text-[#D1D5DB]">•</span>
              <span className="text-[#9CA3AF] font-normal">{formattedTime}</span>
            </>
          )}
        </div>

        {/* Main Content Box */}
        <div
          className={clsx(
            "rounded-2xl px-4 py-3.5 sm:px-5 sm:py-4 relative group transition-all text-xs sm:text-sm leading-relaxed",
            isUser
              ? "bg-gradient-to-r from-[#3461D6] to-[#547EEA] text-white rounded-tr-xs shadow-sm shadow-[#3461D6]/20 font-normal"
              : message.isError
                ? "bg-[#FFE9D0]/50 border border-[#E38A29]/30 text-[#0F172A] rounded-tl-xs shadow-2xs"
                : "bg-white border border-[#E5E7EB] text-[#1F2937] rounded-tl-xs shadow-2xs"
          )}
        >
          <p className="whitespace-pre-wrap">{message.content}</p>

          {/* Assistant Voice Read-out Button */}
          {!isUser && !message.isError && (
            <button
              onClick={handleSpeak}
              type="button"
              className={clsx(
                "absolute right-2.5 top-2.5 p-1.5 rounded-lg border transition-all",
                isSpeaking
                  ? "bg-[#3461D6]/10 text-[#3461D6] border-[#3461D6]/30 opacity-100"
                  : "text-[#9CA3AF] hover:text-[#3461D6] bg-white/90 hover:bg-[#F3F4F6] border-[#E5E7EB] opacity-0 group-hover:opacity-100"
              )}
              title={isSpeaking ? "Stop reading" : "Read aloud"}
            >
              {isSpeaking ? (
                <VolumeX className="w-3.5 h-3.5 animate-pulse text-[#3461D6]" />
              ) : (
                <Volume2 className="w-3.5 h-3.5" />
              )}
            </button>
          )}

          {/* Sources & Citations */}
          {message.sources && message.sources.length > 0 && (
            <div className="mt-3.5 pt-3 border-t border-[#E5E7EB]">
              <p className="text-[11px] font-bold text-[#6B7280] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FileText className="w-3 h-3 text-[#4ABBE3]" /> Verified Sources
              </p>
              <div className="flex flex-wrap gap-1.5">
                {message.sources.map((source, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 text-[11px] bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] px-2.5 py-1 rounded-lg border border-[#D1D5DB] font-medium transition-colors"
                  >
                    <span>{source.title}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const SuggestionCard = ({ text, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(text)}
    className="group flex items-start gap-3 p-3.5 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] hover:border-[#547EEA]/70 rounded-xl text-left transition-all duration-200 shadow-2xs hover:shadow-xs"
  >
    <div className="w-7 h-7 rounded-lg bg-[#FFE9D0] text-[#E38A29] group-hover:bg-[#E38A29] group-hover:text-white flex items-center justify-center shrink-0 mt-0.5 transition-colors">
      <Sparkles className="w-3.5 h-3.5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs sm:text-sm font-medium text-[#374151] group-hover:text-[#0F172A] transition-colors leading-snug">
        {text}
      </p>
    </div>
    <ArrowRight className="w-3.5 h-3.5 text-[#9CA3AF] group-hover:text-[#3461D6] group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
  </button>
);

const VoiceVisualizer = ({ isActive, volume }) => {
  return (
    <div className="flex items-center gap-1 px-1.5 h-5">
      {[1, 2, 3, 4, 5].map((bar) => (
        <motion.div
          key={bar}
          animate={{
            height: isActive
              ? Math.max(4, Math.min(20, (volume / 255) * 45 * (bar * 0.25)))
              : 4,
          }}
          className={clsx(
            "w-1 rounded-full transition-all duration-75",
            isActive ? "bg-[#E38A29]" : "bg-[#D1D5DB]"
          )}
        />
      ))}
    </div>
  );
};

const Assistant = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const machineId = searchParams.get("machineId");
  const { user } = useAuthStore();

  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [micVolume, setMicVolume] = useState(0);
  const [selectedMachine, setSelectedMachine] = useState(machineId || "");
  const [isExtracting, setIsExtracting] = useState(false);
  const [isMachineDropdownOpen, setIsMachineDropdownOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const machineDropdownRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const streamRef = useRef(null);

  const isListeningRef = useRef(false);

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition && !recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "en-IN";

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript || interimTranscript) {
          setInput(finalTranscript || interimTranscript);
        }
      };

      recognitionRef.current.onend = () => {
        if (isListeningRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.warn("Recognition restart failed:", e);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        if (
          event.error === "not-allowed" ||
          event.error === "service-not-allowed"
        ) {
          toast.error("Microphone access denied.");
          setIsListening(false);
          isListeningRef.current = false;
        }
      };
    }

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      stopAudioVisualizer();
      window.speechSynthesis.cancel();
    };
  }, []);

  const startAudioVisualizer = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioContextRef.current = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const updateVolume = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setMicVolume(average);
        animationFrameRef.current = requestAnimationFrame(updateVolume);
      };
      updateVolume();
    } catch (err) {
      toast.error("Microphone hardware error.");
    }
  };

  const stopAudioVisualizer = () => {
    if (animationFrameRef.current)
      cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    analyserRef.current = null;
    audioContextRef.current = null;
    streamRef.current = null;
    setMicVolume(0);
  };

  const toggleListening = async () => {
    if (isListening) {
      isListeningRef.current = false;
      recognitionRef.current?.stop();
      stopAudioVisualizer();
      setIsListening(false);
    } else {
      setInput("");
      isListeningRef.current = true;
      await startAudioVisualizer();
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  const { data: machines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => res.data.data),
  });

  const { data: suggestions } = useQuery({
    queryKey: ["suggestions", selectedMachine],
    queryFn: () =>
      selectedMachine
        ? assistantAPI
            .getSuggestions(selectedMachine)
            .then((res) => res.data.data)
        : Promise.resolve([]),
    enabled: !!selectedMachine,
  });

  const chatMutation = useMutation({
    mutationFn: (data) => assistantAPI.chat(data),
    onSuccess: (response) => {
      const data = response.data.data;
      setSessionId(data.sessionId);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response,
          sources: data.sources,
          intent: data.intent,
          timestamp: new Date(),
        },
      ]);
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
          isError: true,
          timestamp: new Date(),
        },
      ]);
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (machineId) setSelectedMachine(machineId);
  }, [machineId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        machineDropdownRef.current &&
        !machineDropdownRef.current.contains(event.target)
      ) {
        setIsMachineDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSend = () => {
    if (!input.trim() || chatMutation.isPending) return;

    const userMessage = {
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    chatMutation.mutate({
      message: input.trim(),
      machineId: selectedMachine || undefined,
      sessionId,
      userRole: user?.role,
    });

    setInput("");
    inputRef.current?.focus();
  };

  const handleExtractSOP = async () => {
    if (messages.length < 2) {
      toast.error("Not enough context.");
      return;
    }
    setIsExtracting(true);
    try {
      const response = await assistantAPI.extractSop({ chatHistory: messages });
      const suggestedSop = response.data.data;
      if (suggestedSop) {
        if (selectedMachine) suggestedSop.machineId = selectedMachine;
        navigate("/sops/create", { state: { initialData: suggestedSop } });
      }
    } catch (error) {
      toast.error("Failed to extract SOP.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput("");
    window.speechSynthesis.cancel();
  };

  const defaultSuggestions = [
    "How do I operate this machine safely?",
    "What are the daily startup procedures?",
    "How do I troubleshoot common errors?",
    "Show me the maintenance schedule",
  ];

  const currentMachineObj = machines?.find((m) => m.id === selectedMachine);

  return (
    <div className="h-full flex-1 flex flex-col min-h-0 bg-white">
      {/* Top Industrial Header & Controls Bar */}
      <div className="p-4 sm:px-6 sm:py-4.5 border-b border-slate-100 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3461D6] via-[#547EEA] to-[#4ABBE3] flex items-center justify-center text-white shadow-md shadow-[#3461D6]/20 shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-lg font-bold text-[#0F172A] tracking-tight">
                AI Operations Copilot
              </h1>
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFE9D0] text-[#E38A29] border border-[#E38A29]/30">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E38A29] animate-pulse" />
                Online
              </span>
            </div>
            <p className="text-xs text-[#6B7280] font-medium truncate">
              {currentMachineObj
                ? `Targeted context: ${currentMachineObj.name}`
                : "Ask anything about machinery manuals, diagnostics & procedures"}
            </p>
          </div>
        </div>

        {/* Right Actions Toolbar */}
        <div className="flex items-center gap-2 self-end sm:self-auto flex-wrap">
          {/* Create SOP from Chat (Permission Protected) */}
          {messages.length > 1 && (
            <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
              <button
                type="button"
                onClick={handleExtractSOP}
                disabled={isExtracting}
                className="btn text-xs py-2 px-3 rounded-xl border border-[#E38A29]/30 bg-[#FFE9D0]/70 hover:bg-[#FFE9D0] text-[#E38A29] shadow-2xs inline-flex items-center gap-1.5 font-semibold transition-colors"
                title="Synthesize conversation into an SOP"
              >
                {isExtracting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#E38A29]" />
                ) : (
                  <FilePlus className="w-3.5 h-3.5 text-[#E38A29]" />
                )}
                <span>Generate SOP</span>
              </button>
            </HasPermission>
          )}

          {/* Machine Filter Dropdown */}
          <div ref={machineDropdownRef} className="relative">
            <button
              type="button"
              onClick={() => setIsMachineDropdownOpen((prev) => !prev)}
              className={clsx(
                "h-9 px-3 rounded-xl text-xs font-semibold flex items-center justify-between gap-2 border transition-all shadow-2xs min-w-[160px] max-w-[220px]",
                selectedMachine
                  ? "bg-[#3461D6]/10 border-[#3461D6]/40 text-[#3461D6]"
                  : "bg-[#F3F4F6] hover:bg-[#E5E7EB] border-[#E5E7EB] text-[#374151]"
              )}
            >
              <span className="flex items-center gap-1.5 truncate">
                <Cpu className={clsx("w-3.5 h-3.5 shrink-0", selectedMachine ? "text-[#3461D6]" : "text-[#9CA3AF]")} />
                <span className="truncate">{currentMachineObj?.name || "All Machinery"}</span>
              </span>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-[#9CA3AF] transition-transform duration-200 shrink-0",
                  isMachineDropdownOpen && "rotate-180"
                )}
              />
            </button>

            <AnimatePresence>
              {isMachineDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-64 rounded-xl bg-white border border-[#E5E7EB] shadow-xl shadow-[#0F172A]/10 p-1.5 z-30 max-h-64 overflow-y-auto"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMachine("");
                      setIsMachineDropdownOpen(false);
                    }}
                    className={clsx(
                      "flex items-center justify-between w-full px-3 py-2 rounded-lg text-xs font-medium transition-colors",
                      !selectedMachine
                        ? "bg-[#3461D6]/10 text-[#3461D6] font-bold"
                        : "text-[#374151] hover:bg-[#F3F4F6]"
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#9CA3AF] shrink-0" />
                      All Machinery (Global)
                    </span>
                    {!selectedMachine && <Check className="w-3.5 h-3.5 text-[#3461D6]" />}
                  </button>

                  <div className="h-px bg-[#E5E7EB] my-1" />

                  {machines?.map((m) => {
                    const isSelected = selectedMachine === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => {
                          setSelectedMachine(m.id);
                          setIsMachineDropdownOpen(false);
                        }}
                        className={clsx(
                          "flex items-center justify-between w-full px-3 py-2 rounded-lg text-xs transition-colors",
                          isSelected
                            ? "bg-[#3461D6]/10 text-[#3461D6] font-bold"
                            : "text-[#374151] hover:bg-[#F3F4F6] font-medium"
                        )}
                      >
                        <span className="flex items-center gap-2 truncate pr-2">
                          <span className="w-2 h-2 rounded-full bg-[#4ABBE3] shrink-0" />
                          <span className="truncate">{m.name}</span>
                        </span>
                        {isSelected && <Check className="w-3.5 h-3.5 text-[#3461D6] shrink-0" />}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Reset / New Session */}
          <button
            type="button"
            onClick={handleNewChat}
            className="p-2 rounded-xl border border-[#E5E7EB] text-[#6B7280] hover:text-[#0F172A] hover:bg-[#F3F4F6] transition-all shadow-2xs"
            title="Start new conversation"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main Messages Viewport */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6 bg-white">
        {messages.length === 0 ? (
          /* Empty State - Modern Industrial Hero */
          <div className="h-full flex flex-col items-center justify-center text-center px-4 py-8 max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3461D6] via-[#547EEA] to-[#4ABBE3] text-white shadow-xl shadow-[#3461D6]/25 flex items-center justify-center mb-5 ring-4 ring-[#FFE9D0]/60">
              <Bot className="w-8 h-8" />
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-[#0F172A] tracking-tight mb-2">
              How can I assist your operations today?
            </h2>

            <p className="text-xs sm:text-sm text-[#6B7280] max-w-lg mb-6 leading-relaxed">
              Ask questions about operational safety, standard procedures, error diagnostics, or create automated maintenance protocols.
            </p>

            {/* Default Quick Query Prompts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
              {defaultSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="p-3.5 rounded-xl border border-slate-200/90 bg-white hover:bg-blue-50/50 hover:border-[#3461D6]/30 text-left transition-all group flex items-center justify-between shadow-2xs"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 rounded-lg bg-[#FFE9D0]/70 text-[#E38A29] group-hover:scale-105 transition-transform">
                      <Sparkles className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-semibold text-[#1F2937] group-hover:text-[#3461D6] transition-colors">
                      {suggestion}
                    </span>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-[#9CA3AF] group-hover:text-[#3461D6] group-hover:translate-x-0.5 transition-all shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={index}
                message={message}
                isUser={message.role === "user"}
                user={user}
              />
            ))}
            {chatMutation.isPending && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 text-left"
              >
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#3461D6] to-[#4ABBE3] text-white flex items-center justify-center shrink-0 shadow-sm shadow-[#3461D6]/20">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-white border border-slate-200/90 rounded-2xl rounded-tl-sm p-4 shadow-2xs inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#3461D6]" />
                  <span className="text-xs text-[#6B7280] font-medium">
                    Consulting equipment manuals & technical index...
                  </span>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Docked Command & Voice Input Bar */}
      <div className="p-3 sm:p-4 bg-white border-t border-slate-100 shrink-0">
        <div className="max-w-4xl mx-auto space-y-2">
          {/* Active Filter Pill */}
          {selectedMachine && currentMachineObj && (
            <div className="flex items-center justify-between text-[11px] px-1 text-[#6B7280] font-medium">
              <span className="inline-flex items-center gap-1 text-[#3461D6]">
                <Cpu className="w-3 h-3 text-[#4ABBE3]" />
                Querying exclusively for <strong>{currentMachineObj.name}</strong>
              </span>
              <button
                type="button"
                onClick={() => setSelectedMachine("")}
                className="text-[#9CA3AF] hover:text-[#0F172A] underline font-normal cursor-pointer"
              >
                Clear machine filter
              </button>
            </div>
          )}

          {/* Unified Input Card - Compact Single Line */}
          <div className="bg-white border border-slate-200 focus-within:border-[#3461D6] focus-within:ring-2 focus-within:ring-[#3461D6]/20 rounded-2xl px-3 py-1.5 transition-all shadow-xs flex items-center gap-2">
            {/* Voice Visualizer Indicator if listening */}
            {isListening && (
              <div className="shrink-0 flex items-center gap-1.5 bg-[#FFE9D0] border border-[#E38A29]/40 px-2 py-1 rounded-lg">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E38A29] animate-ping" />
                <span className="text-[10px] font-bold text-[#E38A29] hidden sm:inline">Listening</span>
                <VoiceVisualizer isActive={isListening} volume={micVolume} />
              </div>
            )}

            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={
                selectedMachine && currentMachineObj
                  ? `Ask about ${currentMachineObj.name}...`
                  : "Ask a question about machine operations, errors, or procedures..."
              }
              className="flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 text-xs sm:text-sm text-[#1F2937] placeholder-[#9CA3AF] py-1 px-1 min-w-0"
            />

            <div className="flex items-center gap-1.5 shrink-0">
              {/* Microphone Toggle Button */}
              <button
                type="button"
                onClick={toggleListening}
                className={clsx(
                  "p-1.5 sm:p-2 rounded-xl transition-all border",
                  isListening
                    ? "bg-[#E38A29] text-white border-[#E38A29] shadow-sm shadow-[#E38A29]/25 animate-pulse"
                    : "text-[#6B7280] hover:text-[#0F172A] bg-white hover:bg-[#F3F4F6] border-[#E5E7EB] shadow-2xs"
                )}
                title={isListening ? "Stop listening" : "Start voice input"}
              >
                {isListening ? (
                  <MicOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                ) : (
                  <Mic className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                )}
              </button>

              {/* Send Button */}
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || chatMutation.isPending}
                className="text-xs py-1.5 px-3 sm:py-2 sm:px-3.5 rounded-xl bg-gradient-to-r from-[#3461D6] to-[#547EEA] hover:from-[#2e54be] hover:to-[#436cd8] text-white shadow-md shadow-[#3461D6]/20 inline-flex items-center gap-1.5 font-semibold disabled:opacity-40 transition-all cursor-pointer"
              >
                <span>Send</span>
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <p className="text-[11px] text-[#9CA3AF] text-center">
            ConvoLink AI provides guidance synthesized from operational manuals. Always verify critical safety lockouts.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Assistant;
